import { useCallback, useEffect, useState } from "react";
import { Copy, Dice5, Plug, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import type { ClientCreateRequest, GenericResponse } from "@/lib/types";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";

function generateApiKey(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function TcpClientsPage() {
  const [clients, setClients] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [clientId, setClientId] = useState("");
  const [apiKey, setApiKey] = useState(() => generateApiKey());
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<{ clients: string[] }>("/api/clients");
      setClients((resp.clients ?? []).slice().sort());
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = clientId.trim();
    const key = apiKey.trim();
    if (!id || !key) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const body: ClientCreateRequest = { client_id: id, api_key: key };
      const resp = await api.post<GenericResponse>("/api/clients", body);
      if (resp.success) {
        setFeedback({ kind: "success", text: t("Client „{id}\" angelegt — API-Key jetzt sichern.", { id }) });
        setClientId("");
        setApiKey(generateApiKey());
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Anlegen fehlgeschlagen") });
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm(t("Client „{id}\" wirklich löschen? Bestehende Verbindungen brechen ab.", { id }))) return;
    try {
      await api.delete<GenericResponse>(`/api/clients/${encodeURIComponent(id)}`);
      setFeedback({ kind: "success", text: t("Client „{id}\" gelöscht.", { id }) });
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Löschen fehlgeschlagen") });
    }
  };

  const copyKey = () => {
    void navigator.clipboard.writeText(apiKey).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("TCP-Client anlegen")}
        description={t("Client-ID und API-Key authentifizieren Sensoren am TCP-Endpoint.")}
      >
        {/* lg statt md: schmaler Content-Bereich (Sidebar) würde die dritte
            Spalte (Anlegen-Button) abschneiden. */}
        <form onSubmit={onCreate} className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_2fr_auto]">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Client-ID")}
            </span>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={t("z.B. sensor-a4")}
              required
              className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("API-Key")}
            </span>
            <div className="flex gap-1">
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                required
                className="h-9 flex-1 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setApiKey(generateApiKey())}
                title={t("Neuen Key generieren")}
                className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted"
              >
                <Dice5 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={copyKey}
                title={t("Key kopieren")}
                className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-9 items-center gap-1.5 self-end rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
          >
            <Plug className="h-3.5 w-3.5" /> {submitting ? t("Anlegen…") : t("Anlegen")}
          </button>
        </form>
      </Card>

      <Card title={t("Bestehende Clients ({n})", { n: clients.length })}>
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : clients.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("Noch keine TCP-Clients angelegt.")}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {clients.map((id) => (
              <li
                key={id}
                className="flex items-center justify-between gap-3 py-2.5 text-sm first:pt-0 last:pb-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Plug className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate font-mono text-xs">{id}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onDelete(id)}
                  aria-label={t("Client {id} löschen", { id })}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("Löschen")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
